//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';

import {
  IPartialState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasFeature,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';

// Sets default values for state data
function ListeningStateFactory(state: IPartialState) {
  const accessFn = state.data?.hasAccess ?? (() => true);
  const data = {
    title: 'Listening',
    navGroup: NavGroup.LISTENING,
    ...state.data,
    hasAccess: () => hasFeature(FeatureNames.SocialListening) && accessFn(),
  };
  return StateFactory({
    ...state,
    data,
  });
}

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  const isSocialListeningEnabled = hasFeature(FeatureNames.SocialListening);
  const isSocialListeningComeptitorsEnabled = hasFeature(
    FeatureNames.SocialListeningCompetitor,
  );

  if (!isSocialListeningEnabled) {
    urlRouterProvider.when('/listening/*', '/');
  }
  if (!isSocialListeningComeptitorsEnabled) {
    urlRouterProvider.when('/listening/competitors/*', '/');
  } else {
    urlRouterProvider.when(
      '/listening/competitors',
      '/listening/competitors/performance',
    );
  }
}

export const states = {
  listening: ListeningStateFactory({
    url: '/listening',
    template: componentNameToTemplate('listeningPage'),
    resolve: {
      ...lazyLoadModule(
        'curalate.listening',
        () => import('@listening/listeningApp'),
      ),
    },
    redirectTo: 'listening.brand',
  }),

  'listening.brand': ListeningStateFactory({
    url: '/brand-health',
    data: {
      title: 'Listening: BrandHealth',
      navGroup: NavGroup.LISTENING,
    },
    redirectTo: 'listening.brand-health-performance',
  }),

  'listening.brand-health-performance': ListeningStateFactory({
    url: '/brand-health/performance',
    data: {
      title: 'Listening: Brand Health Performance',
      navGroup: NavGroup.LISTENING,
    },
  }),

  'listening.brand-health-wordcloud': ListeningStateFactory({
    url: '/brand-health/wordcloud',
    data: {
      title: 'Listening: Brand health Hashtags',
      navGroup: NavGroup.LISTENING,
    },
  }),

  'listening.brand-health-media': ListeningStateFactory({
    url: '/brand-health/media',
    data: {
      title: 'Listening: Brand Health Media',
      navGroup: NavGroup.LISTENING,
    },
  }),

  'listening.topics-summary': ListeningStateFactory({
    url: '/topics/summary',
    data: {
      title: 'Listening: Topics Summary',
      navGroup: NavGroup.LISTENING,
    },
  }),

  'listening.topics': ListeningStateFactory({
    url: '/topics/:topicId',
    data: {
      title: 'Listening: Topics',
      navGroup: NavGroup.LISTENING,
    },
    redirectTo: 'listening.topics-performance',
  }),

  'listening.topics-performance': ListeningStateFactory({
    url: '/topics/:topicId/performance',
    data: {
      title: 'Listening: Topics Performance',
      navGroup: NavGroup.LISTENING,
    },
  }),
  'listening.topics-wordcloud': ListeningStateFactory({
    url: '/topics/:topicId/wordcloud',
    data: {
      title: 'Listening: Topics Wordcloud',
      navGroup: NavGroup.LISTENING,
    },
  }),

  'listening.topics-media': ListeningStateFactory({
    url: '/topics/:topicId/media',
    data: {
      title: 'Listening:Topics Media',
      navGroup: NavGroup.LISTENING,
    },
  }),

  'listening.competitors': ListeningStateFactory({
    url: '/competitors',
    data: {
      title: 'Listening: Competitors',
      navGroup: NavGroup.LISTENING,
      hasAccess: () => hasFeature(FeatureNames.SocialListeningCompetitor),
    },
  }),

  'listening.competitors-performance': ListeningStateFactory({
    url: '/competitors/performance',
    data: {
      title: 'Listening: Competitors Performance',
      navGroup: NavGroup.LISTENING,
      hasAccess: () => hasFeature(FeatureNames.SocialListeningCompetitor),
    },
  }),

  'listening.competitors-wordcloud': ListeningStateFactory({
    url: '/competitors/wordcloud',
    data: {
      title: 'Listening: Competitors Wordcloud',
      navGroup: NavGroup.LISTENING,
      hasAccess: () => hasFeature(FeatureNames.SocialListeningCompetitor),
    },
  }),

  'listening.competitors-media': ListeningStateFactory({
    url: '/competitors/media',
    data: {
      title: 'Listening: Competitors Media',
      navGroup: NavGroup.LISTENING,
      hasAccess: () => hasFeature(FeatureNames.SocialListeningCompetitor),
    },
  }),
};
